
import { defineComponent } from '@vue/composition-api';

export default defineComponent({
  name: 'Notification',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String as () => 'success' | 'error' | 'warning' | 'info',
      default: 'info'
    },
    duration: {
      type: Number,
      default: null
    },
    closable: {
      type: Boolean,
      default: true
    },
    showTimer: {
      type: Boolean,
      default: false
    },
    usePrimaryColor: {
      type: Boolean,
      default: false
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      default: null
    },
    actions: {
      type: Array as () => Array<{
        label: string;
        callback: (event?: Event) => void;
        variant?: 'primary' | 'secondary' | 'danger' | 'success';
      }>,
      default: () => []
    },
    onClick: {
      type: Function,
      default: null
    },
    persistent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      timeRemaining: 0,
      timerInterval: null as number | null,
      internalExpanded: false
    };
  },
  computed: {
    timerProgress(): number {
      if (!this.duration || this.duration <= 0) return 100;
      // Calcola la percentuale: da 100% (inizio) a 0% (fine)
      // Lo slider parte da destra e si svuota verso sinistra
      return Math.max(
        0,
        Math.min(100, (this.timeRemaining / this.duration) * 100)
      );
    },
    timeRemainingSeconds(): number {
      return Math.ceil(this.timeRemaining / 1000);
    },
    isMobile(): boolean {
      return window.innerWidth < 768; // md breakpoint
    },
    isExpandedComputed(): boolean {
      // Usa la prop se fornita, altrimenti usa lo stato interno
      return this.isExpanded !== undefined
        ? this.isExpanded
        : this.internalExpanded;
    }
  },
  watch: {
    isExpanded(newVal: boolean) {
      // Sincronizza lo stato interno quando la prop cambia
      if (newVal !== undefined) {
        this.internalExpanded = newVal;
      }
    },
    show(newVal: boolean) {
      // Se la notifica è persistente, non avviare il timer
      if (this.persistent) {
        if (!newVal) {
          this.clearTimer();
          this.internalExpanded = false;
        }
        return;
      }

      if (newVal && this.duration && this.duration > 0) {
        if (this.showTimer) {
          // Timer con slider
          this.timeRemaining = this.duration;
          this.clearTimer();
          this.timerInterval = window.setInterval(() => {
            this.timeRemaining -= 100;
            if (this.timeRemaining <= 0) {
              this.timeRemaining = 0;
              this.clearTimer();
              this.handleClose();
            }
          }, 100);
        } else {
          // Timer semplice senza slider
          setTimeout(() => {
            this.handleClose();
          }, this.duration);
        }
      } else {
        this.clearTimer();
        // Reset expanded state quando la notifica viene nascosta
        if (!newVal) {
          this.internalExpanded = false;
        }
      }
    }
  },
  methods: {
    handleClose() {
      this.clearTimer();
      this.internalExpanded = false;
      this.$emit('close');
    },
    clearTimer() {
      if (this.timerInterval !== null) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    handleClick(event: Event) {
      const target = event.target as HTMLElement;
      // Non emettere click se si clicca sul pulsante di chiusura o sui pulsanti interni
      if (
        target.closest('button') &&
        (target.closest('button[type="button"]') || target.closest('.sr-only'))
      ) {
        return;
      }
      // Non emettere click se si clicca su un'azione (bottone delle azioni)
      if (target.closest('button') && this.actions && this.actions.length > 0) {
        // Le azioni gestiscono il loro click tramite @click.stop
        return;
      }
      // Su mobile, emetti evento expand quando viene cliccata
      // Ma non espandere se si clicca sul pulsante di chiusura o sui pulsanti interni
      if (this.isMobile && !this.isExpandedComputed) {
        // Non espandere se si clicca su un pulsante o un link
        if (
          !target.closest('button') &&
          !target.closest('a') &&
          !target.closest('[role="button"]')
        ) {
          event.preventDefault();
          event.stopPropagation();
          this.$emit('expand');
        }
      }
      // Chiama onClick se fornito
      if (this.onClick && typeof this.onClick === 'function') {
        this.onClick(event);
      }
      // Emetti sempre un evento click (escludendo pulsanti di chiusura e azioni)
      if (
        !target.closest('button[type="button"]') &&
        !target.closest('.sr-only')
      ) {
        this.$emit('click', event);
      }
    }
  },
  beforeDestroy() {
    this.clearTimer();
  }
});
